import React from 'react';
import { Alert, AlertTitle, Box, Button, Typography, LinearProgress } from '@mui/material';
import { WorkspacePremiumOutlined, UpgradeOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/useAuthStore';

const TrialBanner: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();

  // Ne pas afficher si pas de période d'essai ou si abonnement payant actif
  if (!user?.trialEndDate) {
    return null;
  }

  // Calculer le nombre de jours restants
  const trialEnd = new Date(user.trialEndDate);
  const now = new Date();
  const diffTime = trialEnd.getTime() - now.getTime();
  const daysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  // Ne pas afficher si la période d'essai est expirée
  if (daysRemaining <= 0) {
    return null;
  }

  // Calculer le pourcentage de temps écoulé (sur 30 jours)
  const totalDays = 30;
  const daysElapsed = totalDays - daysRemaining;
  const progress = (daysElapsed / totalDays) * 100;

  // Déterminer la couleur selon les jours restants
  const getSeverity = (): 'info' | 'warning' | 'error' => {
    if (daysRemaining > 14) return 'info';
    if (daysRemaining > 7) return 'warning';
    return 'error';
  };

  const getMessage = () => {
    if (daysRemaining > 14) {
      return `Vous profitez de la période d'essai Premium ! ${daysRemaining} jours restants.`;
    } else if (daysRemaining > 7) {
      return `Attention : Plus que ${daysRemaining} jours de période d'essai Premium restants.`;
    } else {
      return `Derniers jours ! Votre période d'essai Premium se termine dans ${daysRemaining} jour${
        daysRemaining > 1 ? 's' : ''
      }.`;
    }
  };

  return (
    <Alert
      severity={getSeverity()}
      icon={<WorkspacePremiumOutlined fontSize="large" />}
      sx={{
        mb: 3,
        borderRadius: 2,
        '& .MuiAlert-message': {
          width: '100%',
        },
      }}
      action={
        daysRemaining <= 14 ? (
          <Button
            color="inherit"
            size="small"
            startIcon={<UpgradeOutlined />}
            onClick={() => navigate('/subscription')}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Choisir un plan
          </Button>
        ) : undefined
      }
    >
      <AlertTitle sx={{ fontWeight: 600 }}>
        Période d'essai Premium{' '}
        {user.subscriptionTier === 'PREMIUM' && <span>(Version Premium)</span>}
      </AlertTitle>
      <Typography variant="body2" sx={{ mb: 1 }}>
        {getMessage()}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
        <Box sx={{ flex: 1 }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            color={getSeverity()}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>
        <Typography variant="caption" sx={{ fontWeight: 600, minWidth: '60px' }}>
          {Math.round(progress)}%
        </Typography>
      </Box>
      <Typography variant="caption" display="block" sx={{ mt: 1, opacity: 0.8 }}>
        Accès complet à toutes les fonctionnalités : Devis, Factures, Photos, Signature
        électronique, Paiement CB, Gestion Comptabilité
      </Typography>
    </Alert>
  );
};

export default TrialBanner;
